<div>
  <div class="prose">
    <h1>API Job Logs</h1>
  </div>

  <!-- Filters -->
  <div class="mt-6 rounded-lg border border-base-content/20 bg-base-100 p-4">
    <h2 class="text-lg font-semibold">Filters</h2>
    <div
      class="flex flex-col space-x-0 space-y-2 sm:flex-row sm:space-x-4 sm:space-y-0"
    >
      <!-- Date Range Filter -->
      <div class="form-control">
        <label class="label">
          <span class="label-text">Start Date</span>
        </label>
        <input
          type="date"
          class="input input-bordered"
          [(ngModel)]="startDateFilter"
          (change)="applyFilters()"
        />
      </div>
      <div class="form-control">
        <label class="label">
          <span class="label-text">End Date</span>
        </label>
        <input
          type="date"
          class="input input-bordered"
          [(ngModel)]="endDateFilter"
          (change)="applyFilters()"
        />
      </div>

      <!-- Charged Filter -->
      <div class="form-control">
        <label class="label">
          <span class="label-text">Charged</span>
        </label>
        <select
          class="select select-bordered"
          [(ngModel)]="chargedFilter"
          (change)="applyFilters()"
        >
          <option [value]="undefined">All</option>
          <option [value]="true">Yes</option>
          <option [value]="false">No</option>
        </select>
      </div>

      <div class="flex items-end space-x-4 pl-0 sm:pl-4">
        <button class="btn btn-neutral mt-2 sm:mt-0" (click)="clearFilters()">
          Clear Filters
        </button>
      </div>
    </div>
  </div>

  @if (errorFetchingJobLogs) {
    <div class="mt-4">
      <p class="text-error">{{ errorFetchingJobLogs }}</p>
    </div>
  } @else if (loadingJobLogs) {
    <div class="mt-4 flex justify-center">
      <div class="loading loading-spinner loading-lg"></div>
    </div>
  } @else {
    @if (filteredJobLogs.length > 0) {
      <div class="mt-8">
        <div class="mb-4 flex justify-between">
          <div class="self-end text-sm text-base-content/70">
            Showing {{ (currentPage - 1) * pageSize + 1 }} to
            {{ getMaxDisplayIndex() }} of {{ total }} job log{{
              total > 1 ? 's' : ''
            }}
          </div>
          <div class="flex gap-2">
            @if (selectedJobLogIds.size > 0) {
              <button
                class="btn btn-neutral btn-sm"
                (click)="clearCheckedSelections()"
                [disabled]="loadingJobLogs"
              >
                Clear Checked
              </button>
            }
            <button
              class="btn btn-primary btn-sm"
              (click)="exportJobLogs()"
              [disabled]="loadingJobLogs || filteredJobLogs.length === 0"
            >
              Export {{ getExportCount() }} Log{{
                getExportCount() !== 1 ? 's' : ''
              }}
            </button>
          </div>
        </div>

        <div
          class="hidden overflow-x-auto rounded-lg border border-base-content/20 sm:block"
        >
          <table
            class="table table-zebra table-pin-rows table-pin-cols table-lg static"
            [class.table-sm]="tableSpacing === 'compact'"
            [class.table-md]="tableSpacing === 'normal'"
            [class.table-lg]="tableSpacing === 'comfortable'"
          >
            <thead class="text-base font-medium">
              <tr>
                <td class="w-1">Date</td>
                <td class="w-80">Job GUID</td>
                <td>Filename</td>
                <td class="w-1 text-right">Record Count</td>
                <td class="w-1 text-right">Amount</td>
                <td class="w-1">Charged</td>
                <td class="w-1"></td>
              </tr>
            </thead>
            <tbody>
              @for (log of paginatedJobLogs; track log.id) {
                <tr class="text-base">
                  <td class="w-1">{{ formatDate(log.transaction_date) }}</td>
                  <td class="w-80 break-all">{{ log.job_guid }}</td>
                  <td>{{ log.filename }}</td>
                  <td class="w-1 text-right">
                    {{ log.record_count | number }}
                  </td>
                  <td class="w-1 text-right">
                    {{ log.transaction_amount | currency }}
                  </td>
                  <td class="w-1 text-center">
                    @if (log.charged) {
                      <span class="badge badge-success">Yes</span>
                    } @else {
                      <span class="badge badge-ghost">No</span>
                    }
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      class="checkbox"
                      [checked]="isJobLogSelected(log.id)"
                      (change)="toggleJobLogSelection(log.id)"
                    />
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <!-- Mobile Job Logs -->
        <div class="sm:hidden">
          @for (log of paginatedJobLogs; track log.id) {
            <div
              class="my-2 w-full rounded-lg border border-base-content/20 p-4"
            >
              <div class="flex">
                <p class="pb-1 pr-3 text-base-content/70">Date:</p>
                <p>{{ formatDate(log.transaction_date) }}</p>
              </div>
              <div class="flex">
                <p class="pb-1 pr-3 text-base-content/70">Job GUID:</p>
                <p class="break-all font-mono text-sm">{{ log.job_guid }}</p>
              </div>
              <div class="flex">
                <p class="pb-1 pr-3 text-base-content/70">Filename:</p>
                <p>{{ log.filename }}</p>
              </div>
              <div class="flex">
                <p class="pb-1 pr-3 text-base-content/70">Record Count:</p>
                <p>{{ log.record_count | number }}</p>
              </div>
              <div class="flex">
                <p class="pb-1 pr-3 text-base-content/70">Amount:</p>
                <p>{{ log.transaction_amount | currency }}</p>
              </div>
              <div class="flex">
                <p class="pb-1 pr-3 text-base-content/70">Charged:</p>
                <p>
                  @if (log.charged) {
                    <span class="badge badge-success">Yes</span>
                  } @else {
                    <span class="badge badge-ghost">No</span>
                  }
                </p>
              </div>
              <div class="mt-2 flex items-center">
                <input
                  type="checkbox"
                  class="checkbox"
                  [checked]="isJobLogSelected(log.id)"
                  (change)="toggleJobLogSelection(log.id)"
                />
              </div>
            </div>
          }
        </div>

        <!-- Pagination -->
        <div class="mt-6 flex justify-center">
          <app-pagination
            [spacing]="tableSpacing"
            [pageSize]="pageSize"
            [currentPage]="currentPage"
            [totalPages]="totalPages"
            (pageChanged)="changePage($event)"
            (settingsChanged)="handleSettingsChanged($event)"
          ></app-pagination>
        </div>
      </div>
    } @else {
      <div class="mt-8">
        <p class="italic text-base-content/80">No job logs found.</p>
      </div>
    }
  }
</div>
